import DefaultLayout from 'hellosign/common/layout/default-layout';
import React from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router';
import { RouteBoundary } from 'signer-app/parts/error-boundary';
import { useHSUser } from './common/hs-user';
import { RedirectTo } from './common/utils/redirect';
import { LabeledRoute } from './components/labeled-route';
import { useUnsafePageProps } from './slices/page-props';
import { StoreShape } from './redux/namespaces';

const TemplateGallery = React.lazy(async () => ({
  default: (await import('hellospa/components/template-gallery'))
    .TemplateGallery,
}));
const ViewGalleryTemplate = React.lazy(async () => ({
  default: (await import('template-gallery')).ViewTemplate,
}));
const BrowseTemplateGallery = React.lazy(async () => ({
  default: (await import('hellospa/components/template-gallery'))
    .SignBrowseTemplateGallery,
}));
const TemplatesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-templates" */ 'hellospa/page/templates'
    ),
);
const EditorPage = React.lazy(
  () => import(/* webpackChunkName: "hellospa-editor" */ './page/editor'),
);
const AdminPage = React.lazy(
  () => import(/* webpackChunkName: "hellospa-admin" */ './page/admin'),
);
const MoarPage = React.lazy(
  () => import(/* webpackChunkName: "hellospa-moar" */ './page/moar'),
);
const HomePage = React.lazy(
  () => import(/* webpackChunkName: "hellospa-home" */ './page/home'),
);
const DropboxConnectPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxconnect" */ './page/dropbox/connect'
    ),
);
const DropboxErrorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxerrorpage" */ './components/deep-integration/error-page'
    ),
);
const DropboxSsoPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxssopage" */ './components/deep-integration/sso-page'
    ),
);
const DropboxConfirmStorageAccess = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-confirmstorageaccess" */ './components/deep-integration/storage-access/confirm'
    ),
);
const DropboxRequestStorageAccess = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-requeststorageaccess" */ './components/deep-integration/storage-access/request'
    ),
);
const SafetyFirst = React.lazy(
  () =>
    import(/* webpackChunkName: "hellospa-safetyfirst" */ './page/safetyfirst'),
);
const UserPostSignUp = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-postsignup" */ './page/user-post-sign-up'
    ),
);
const RecoverPassword = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-recoverPassword" */ './page/recover-password-page'
    ),
);
const NothingToSignPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-nothingToSign" */ 'hellospa/page/nothing-to-sign-page'
    ),
);
const SetPassword = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-setPassword" */ './page/set-password-page'
    ),
);
const OnboardingUpsell = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-onboardingUpsell" */ './page/onboarding-upsell-page'
    ),
);
const InformationFlagPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-emailactivation" */ './page/information-flag'
    ),
);
const HubspotTemplatePage = React.lazy(
  () =>
    import(/* webpackChunkName: "hellospa-hubspot" */ './components/hubspot'),
);
const HubspotRequestComplete = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-hubspotclose" */ './components/hubspot/hubspot-close'
    ),
);
const IntegrationStatusPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-integrationstatus" */ './components/integration/workflow-status'
    ),
);
const IntegrationCreateOneTimeDoc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-integrationcreateonetimedoc" */ './components/integration/create-one-time-doc'
    ),
);
const IntegrationCreateTemplate = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-integrationcreatetemplate" */ './components/integration/create-template'
    ),
);
const IntegrationSendTemplatePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationSendTemplate" */ './components/integration/send-template'
    ),
);
const IntegrationEditTemplatePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationEditTemplate" */ './components/integration/edit-template'
    ),
);
const IntegrationSendRemindersPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationSendReminders" */ './components/integration/send-reminders'
    ),
);
const IntegrationCancelSignatureRequestPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationCancelSignatureRequest" */ './components/integration/cancel-signature-request'
    ),
);
const PrepAndSendPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-prepandsend" */ './page/prep-and-send'
    ),
);
const DropboxLoginVerificationPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxloginverification" */ './page/dropbox/login-verification'
    ),
);
const CancelSubscriptionPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxloginverification" */ './page/cancel-subscription'
    ),
);
const OfficeWordSelfSign = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-officewordselfsign" */ './components/microsoft/selfsign'
    ),
);
const OfficeWordSendSignature = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-officewordsendsignature" */ './components/microsoft/sendsignature'
    ),
);
const OfficeWordSettings = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-officewordsettings" */ './components/microsoft/settings'
    ),
);
const ChooseSignupPlanPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-choosesignupplanpage" */ './page/choose-signup-plan'
    ),
);
const ThrowAFrontendErrorPleasePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-throwAFrontendErrorPlease" */ './page/throw-a-frontend-error-please'
    ),
);
const PaymentPage = React.lazy(
  () => import(/* webpackChunkName: "hellospa-payment" */ './page/payment'),
);
const IdentVerificationPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-identverification" */ './page/ident-verification'
    ),
);
const SignatureRequestsLandingPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-signature-requests" */ './components/signature-requests/landing'
    ),
);
const HelpPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-help" */ './components/help/help-page'
    ),
);
const AccountSettings = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-account-settings" */ './page/account-settings'
    ),
);
const SignUpPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-signuppage" */ 'hellospa/page/authentication/progressive-checkout-sign-up-page'
    ),
);
const SignInPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-progressivecheckoutsigninpage" */ 'hellospa/page/authentication/progressive-checkout-sign-in-page'
    ),
);
const JoinTeamPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-jointeampage" */ 'hellospa/page/authentication/join-team-page'
    ),
);
const NoAccessErrorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-noaccesserrorpage" */ 'hellospa/page/no-access-error-page'
    ),
);
const ApiDashboardHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-apidashboard" */ './page/api-dashboard'
    ),
);
const AcceptTermsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-acceptterms" */ 'hellospa/page/authentication/accept-terms-page'
    ),
);
const TransferTemplatesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-templateconverter" */ 'hellospa/page/template-converter'
    ),
);
const SignFaxBridgePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-signfaxbridge" */ 'hellospa/page/sign-fax-bridge'
    ),
);
const VulnerableNewtAuditTrailRenderingIssueReportPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-vulnerablenewtaudittrailrenderingissuereport" */ 'hellospa/page/one-time-report/vulnerable-newt-audit-trail-rendering-issue-report'
    ),
);
const CopaceticMoaAttachmentsReportPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-copaceticmoaattachmentsreport" */ 'hellospa/page/one-time-report/copacetic-moa-attachments-report'
    ),
);
const JointVulnerableNewtCopaceticMoaReportPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-jointvulnerablenewtcopaceticmoareport" */ 'hellospa/page/one-time-report/joint-vulnerable-newt-copacetic-moa-report'
    ),
);
const DocumentPreviewPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-editor-documentpreview" */ 'hellospa/components/editor/document-preview'
    ),
);
function OneTimeReportRouter() {
  const pageKey = useSelector(
    (state: StoreShape) => state.pageProps.data?.pageKey,
  );
  switch (pageKey) {
    case 'vulnerableNewtAuditTrailRenderingIssueReport':
      return <VulnerableNewtAuditTrailRenderingIssueReportPage />;
    case 'copaceticMoaAttachmentsReport':
      return <CopaceticMoaAttachmentsReportPage />;
    case 'jointVulnerableNewtCopaceticMoaReport':
      return <JointVulnerableNewtCopaceticMoaReportPage />;
    default:
      return null;
  }
}
const NotFound = () => {
  // This will trigger our ErrorBoundary and report to Sentry The URL will be
  // part of the report.
  throw new Error('404 not found');
};
// const pathRegex = /\/([^/]+\.php)?/;
const RedirectHome = () => {
  return <RedirectTo url="/" />;
};
function PHPEndpoint() {
  React.useEffect(() => {
    window.location.reload();
  }, []);

  return <></>;
}
/**
 * <Routes holds all the routes for the `hellospa.js` bundle. It should contain
 * ONLY route configuration and should not depend on specific knowledge of the
 * current page.
 *
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764535231213701&cot=14
 */
function Routes() {
  const { isDeepIntegration } = useUnsafePageProps();
  const user = useHSUser();

  // These routes will be accessible by all visitors,
  // regardless of whether or not they're logged in.
  // <Switch doesn't like react fragments, so we have
  // to use an array with keys.
  //
  // These keys only need to be locally unique.
  const commonRoutes = [
    <Route
      key="FrontendError"
      exact
      path="/info/throwAFrontendErrorPlease"
      component={ThrowAFrontendErrorPleasePage}
    />,
    <LabeledRoute
      key="NoAccess"
      label="NoAccess"
      path="/info/noAccess"
      component={NoAccessErrorPage}
    />,
    <LabeledRoute
      key="JoinTeam"
      label="Join team"
      path="/account/confirmTeamInvite"
      component={JoinTeamPage}
    />,
  ];

  if (!user) {
    return (
      <Switch>
        {commonRoutes}
        <LabeledRoute
          label="Create an account"
          path="/account/signUp"
          component={SignUpPage}
        />
        <LabeledRoute
          label="Sign in"
          path="/account/logIn"
          component={SignInPage}
        />
        <LabeledRoute
          label="Sign in"
          path="/account/mobileLogIn"
          component={SignInPage}
        />
        <LabeledRoute
          label="Accept terms"
          path="/account/acceptTerms"
          component={AcceptTermsPage}
        />
        <Route path="/dropbox/connect" component={DropboxConnectPage} />
        <Route
          path="/dropbox/logInVerification"
          component={DropboxLoginVerificationPage}
        />
        <RouteBoundary
          name="DropboxConfirmStorageAccess"
          path="/dropbox/confirmStorageAccess"
          component={DropboxConfirmStorageAccess}
        />
        <RouteBoundary
          name="DropboxError"
          path="/dropbox/error"
          component={DropboxErrorPage}
        />
        <Route
          path="/dropbox/requestStorageAccess"
          component={DropboxRequestStorageAccess}
        />
        <Route path="/dropbox/sso" component={DropboxSsoPage} />
        <Route
          path="/integration/upgradeRequired"
          component={IntegrationStatusPage}
        />
        <Route
          path="/integration/enableThirdPartyCookies"
          component={IntegrationStatusPage}
        />
        <Route path="/integration/status" component={IntegrationStatusPage} />
        <Route
          path="/integration/createOneTimeDoc"
          component={IntegrationCreateOneTimeDoc}
        />
        <Route
          path="/integration/createTemplate"
          component={IntegrationCreateTemplate}
        />
        <Route
          path="/integration/sendTemplate"
          component={IntegrationSendTemplatePage}
        />
        <Route
          path="/integration/editTemplate"
          component={IntegrationEditTemplatePage}
        />
        <Route
          path="/integration/sendReminders"
          component={IntegrationSendRemindersPage}
        />
        <Route
          path="/integration/cancelSignatureRequest"
          component={IntegrationCancelSignatureRequestPage}
        />
        <Route path="/microsoft/selfSign" component={OfficeWordSelfSign} />
        <Route
          path="/microsoft/sendSignature"
          component={OfficeWordSendSignature}
        />
        <Route path="/microsoft/settings" component={OfficeWordSettings} />
        <LabeledRoute
          label="RecoverPassword"
          path="/account/recoverPassword"
          component={RecoverPassword}
        />
        <LabeledRoute
          label="NothingToSign"
          path="/info/nothingToSign"
          component={NothingToSignPage}
        />
        <LabeledRoute
          label="NothingToSign"
          path="/sign/:guid"
          component={NothingToSignPage}
        />
        <Route component={NotFound} />
      </Switch>
    );
  } else {
    return (
      <Switch>
        {commonRoutes}
        <LabeledRoute
          label="ApiDashboard"
          path="/apidashboard"
          component={ApiDashboardHome}
        />
        <LabeledRoute
          label="AccountSettings"
          path="/accountSettings"
          component={AccountSettings}
        />
        <LabeledRoute
          label="SafetyFirst"
          path="/account/safetyfirst"
          component={SafetyFirst}
        />
        <LabeledRoute
          label="PostSignUpForm"
          path="/account/postSignUp"
          component={UserPostSignUp}
        />
        <LabeledRoute
          label="SetPassword"
          path="/onboarding/setPassword"
          component={SetPassword}
        />
        <LabeledRoute
          label="OnboardingUpsell"
          path="/onboarding/upsell"
          component={OnboardingUpsell}
        />
        <LabeledRoute
          label="CancelSubscription"
          path="/account/cancelSubscription"
          component={CancelSubscriptionPage}
        />
        <LabeledRoute
          label="EmailActivation"
          path="/account/emailActivation"
          component={InformationFlagPage}
        />
        <LabeledRoute label="Admin" path="/admin" exact component={AdminPage} />
        <LabeledRoute label="Admin" path="/admin/*" component={AdminPage} />
        <LabeledRoute
          label="Editor"
          path="/editor/prepare"
          component={EditorPage}
        />
        <LabeledRoute
          label="Editor"
          path="/editor/editor"
          component={EditorPage}
        />
        <LabeledRoute label="Home" path="/home/index" component={HomePage} />
        <LabeledRoute
          label="ChooseSignMethod"
          path="/home/chooseSignMethod"
          component={HomePage}
        />
        <LabeledRoute label="Help" path="/home/help" component={HelpPage} />
        <LabeledRoute
          label="Documents"
          path="/home/manage"
          component={SignatureRequestsLandingPage}
        />
        <LabeledRoute
          label="HubspotTemplate"
          path="/hubspot/selectTemplate"
          component={HubspotTemplatePage}
        />
        <LabeledRoute
          label="HubspotComplete"
          path="/hubspot/close"
          component={HubspotRequestComplete}
        />
        <LabeledRoute
          label="ChoosePlan"
          path="/info/chooseSignupPlan"
          exact
          component={ChooseSignupPlanPage}
        />
        <LabeledRoute
          label="Payment"
          path="/info/payment"
          component={PaymentPage}
        />
        <LabeledRoute
          label="MoarFaxes"
          path="/internal/moar"
          exact
          component={MoarPage}
        />
        <LabeledRoute
          label="Templates"
          path="/home/createReusableDocs"
          exact
          component={TemplatesPage}
        />
        <LabeledRoute
          label="IdentSuccess"
          path="/info/identSuccess"
          component={IdentVerificationPage}
        />
        <LabeledRoute
          label="IdentIncomplete"
          path="/info/identIncomplete"
          component={IdentVerificationPage}
        />
        <LabeledRoute
          label="Templates"
          path="/home/transferTemplates"
          exact
          component={TransferTemplatesPage}
        />
        <LabeledRoute
          label="SignFaxBridge"
          path="/signFax/bridge"
          component={SignFaxBridgePage}
        />
        <LabeledRoute
          label="OneTimeReport"
          path="/report/:accessToken"
          exact
          component={OneTimeReportRouter}
        />
        <LabeledRoute
          label="DocumentPreview"
          path="/editor/view"
          component={DocumentPreviewPage}
        />
        <LabeledRoute
          label={`prep-and-send${isDeepIntegration ? '.deep-integration' : ''}`}
          path="/prep-and-send/:transmissionGroupGuid"
          component={PrepAndSendPage}
        />

        {/*
          This route can't simply use renderHome, because it has
          page-specific parameters (3rd parameter of prepareSPA). So I have
          to use this awkward hack to make a full page redirect so the BE
          will provide the data needed to render the homepage.

          Accessing this URL from a mobile device with the app will launch
          the app. If a user arrives here without an app or on the desktop,
          it just redirects to the home page.
        */}
        <Route path="/account/mobileApp" component={RedirectHome} />
        <Route path="/template-gallery">
          <DefaultLayout>
            <TemplateGallery>
              <Switch>
                <Route path="/template-gallery/view/:id">
                  <ViewGalleryTemplate />
                </Route>
                <Route path="/template-gallery/browse">
                  <BrowseTemplateGallery />
                </Route>
                <Route path="/template-gallery/copy/:id">
                  <PHPEndpoint />
                </Route>
                <Route path="/template-gallery/use/:id">
                  <PHPEndpoint />
                </Route>
                <Route component={NotFound} />
              </Switch>
            </TemplateGallery>
          </DefaultLayout>
        </Route>
        <Route path="/" exact component={HomePage} />
        <Route component={NotFound} />
      </Switch>
    );
  }
}

export const helloSpaRoutes = <Routes />;
